/* ==========================================================================
   THE THREE WORLDS — entrance lifecycle and world switching.

   Design constraints this file honours:

   - The entrance is revealed by script and hidden in markup. A visitor
     with JS disabled, or where this file fails to load, sees the Couture
     homepage normally and is never trapped behind a layer they cannot
     dismiss.
   - The entrance never blocks a deep link. It is only ever shown when
     the visitor arrived at the site root with no query and no hash.
   - Storage is wrapped: Safari private mode throws on sessionStorage
     access, and an exception here would otherwise leave the overlay up.
   - Focus is moved into the dialog on open and restored on close.
   ========================================================================== */

(function () {
  'use strict';

  var SESSION_KEY = 'at:entrance:seen';
  var DEVICE_KEY = 'at:entrance:seen:device';

  /* ----------------------------------------------------------------------
     Storage that cannot throw
     ---------------------------------------------------------------------- */

  function readFlag(store, key) {
    try {
      return window[store].getItem(key) === '1';
    } catch (e) {
      return false;
    }
  }

  function writeFlag(store, key) {
    try {
      window[store].setItem(key, '1');
    } catch (e) {
      /* Private mode or storage disabled — the entrance simply shows again. */
    }
  }

  function prefersReducedMotion() {
    try {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (e) {
      /* Older in-app webviews have no matchMedia. Assume motion is fine. */
      return false;
    }
  }

  /* ----------------------------------------------------------------------
     The entrance
     ---------------------------------------------------------------------- */

  function initEntrance() {
    var root = document.querySelector('[data-at-entrance]');
    if (!root) return;

    var frequency = root.dataset.frequency || 'session';

    /* Only at the true root. A visitor who followed a link to
       /collections/... is going somewhere specific — do not interrupt
       them — but a query string on the ROOT path is not a deep link.

       An earlier version of this check also bailed on any
       window.location.search, which broke the entrance everywhere it
       mattered most: Shopify's own theme preview always appends
       ?preview_theme_id=..., and the storefront routinely adds
       _ab/_fd/_sc parameters, so the overlay never appeared in preview.
       Marketing links (?utm_source=...) land on the root too, and those
       visitors should absolutely see the entrance.

       A #hash is still respected: it targets a specific part of the
       page, so the visitor has asked to go somewhere precise. */
    var path = window.location.pathname.replace(/\/+$/, '');
    var isRoot = path === '' || /^\/[a-z]{2}(-[a-z]{2})?$/i.test(path);
    if (!isRoot || window.location.hash) return;

    if (frequency === 'session' && readFlag('sessionStorage', SESSION_KEY)) return;
    if (frequency === 'once' && readFlag('localStorage', DEVICE_KEY)) return;

    var lastFocused = document.activeElement;
    var reduced = prefersReducedMotion();

    function focusables() {
      return Array.prototype.slice.call(
        root.querySelectorAll('a[href], button:not([disabled])')
      );
    }

    function open() {
      root.hidden = false;
      document.documentElement.classList.add('at-entrance-open');
      document.body.style.overflow = 'hidden';

      /* Force a frame so the transition from the hidden state runs. */
      if (reduced) {
        root.classList.add('is-open');
      } else {
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () {
            root.classList.add('is-open');
          });
        });
      }

      /* Move focus to the dialog itself rather than the first link, so a
         screen reader announces the message before the destinations. */
      var heading = root.querySelector('[data-entrance-heading]');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        try { heading.focus({ preventScroll: true }); } catch (e) { heading.focus(); }
      }

      document.addEventListener('keydown', onKeydown, true);
    }

    function close() {
      if (root.dataset.closing === '1') return;
      root.dataset.closing = '1';

      if (frequency === 'once') writeFlag('localStorage', DEVICE_KEY);
      else writeFlag('sessionStorage', SESSION_KEY);

      document.removeEventListener('keydown', onKeydown, true);
      root.classList.add('is-closing');

      var finish = function () {
        root.hidden = true;
        root.classList.remove('is-open', 'is-closing');
        root.dataset.closing = '';
        document.documentElement.classList.remove('at-entrance-open');
        document.body.style.overflow = '';
        if (lastFocused && typeof lastFocused.focus === 'function') {
          try { lastFocused.focus({ preventScroll: true }); } catch (e) { lastFocused.focus(); }
        }
      };

      if (reduced) finish();
      else window.setTimeout(finish, 460);
    }

    function onKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        close();
        return;
      }

      /* Trap Tab inside the dialog while it is up. */
      if (event.key !== 'Tab') return;
      var items = focusables();
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* Dismiss control. Following a destination link is a navigation, so
       the flag is written there too — otherwise using the browser Back
       button would replay the entrance. */
    root.addEventListener('click', function (event) {
      if (event.target.closest('[data-entrance-dismiss]')) {
        event.preventDefault();
        close();
        return;
      }

      var world = event.target.closest('a.at-wdest');
      if (world) {
        if (frequency === 'once') writeFlag('localStorage', DEVICE_KEY);
        else writeFlag('sessionStorage', SESSION_KEY);
      }
    });

    /* Clicking the surrounding stage exits, matching the dialog convention. */
    var veil = root.querySelector('.at-entrance__veil');
    if (veil) veil.addEventListener('click', close);

    open();
  }

  /* ----------------------------------------------------------------------
     Mobile world sheet
     ---------------------------------------------------------------------- */

  function initSheet() {
    var trigger = document.querySelector('[data-at-switcher-trigger]');
    var sheet = document.querySelector('[data-at-sheet]');
    if (!trigger || !sheet) return;

    var panel = sheet.querySelector('.at-sheet__panel');
    var scrim = sheet.querySelector('.at-sheet__scrim');
    var reduced = prefersReducedMotion();

    function open() {
      sheet.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';

      var reveal = function () { sheet.classList.add('is-visible'); };
      if (reduced) reveal();
      else window.requestAnimationFrame(function () { window.requestAnimationFrame(reveal); });

      var firstLink = sheet.querySelector('a.at-wdest, .at-wdest');
      if (firstLink && typeof firstLink.focus === 'function') {
        try { firstLink.focus({ preventScroll: true }); } catch (e) {}
      }

      document.addEventListener('keydown', onKeydown, true);
    }

    function close() {
      sheet.classList.remove('is-visible');
      trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', onKeydown, true);

      var finish = function () {
        sheet.classList.remove('is-open');
        document.body.style.overflow = '';
      };

      if (reduced) finish();
      else window.setTimeout(finish, 380);

      try { trigger.focus({ preventScroll: true }); } catch (e) {}
    }

    function onKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        close();
      }
    }

    trigger.addEventListener('click', function () {
      if (sheet.classList.contains('is-open')) close();
      else open();
    });

    if (scrim) scrim.addEventListener('click', close);

    /* Any resize into the desktop breakpoint closes the sheet, so the
       page cannot be left with a locked body scroll. */
    window.addEventListener('resize', function () {
      if (window.innerWidth >= 992 && sheet.classList.contains('is-open')) close();
    });
  }

  function boot() {
    try { initEntrance(); } catch (e) {}
    try { initSheet(); } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
